// Receivables: who owes us, how old it is, and what we are doing about it. The aging ties to the
// AR account; reminders follow the company's schedule (sent on their own at 10:00 a.m., or now from
// here); payments (one across a customer's invoices, F-07), holds and statements are one click from
// each invoice.

import { useEffect, useRef, useState } from 'react';
import type { BooksProps } from './props.ts';
import { arTie, BUCKETS, BUCKET_LABEL, dayText } from './model.ts';
import { downloadStatement } from './statement-pdf.ts';
import type { ArAging, DueReminder } from './types.ts';
import { Empty, Loading, Money, Panel, RowActions, useAct, useQuery, useRowPrimary } from './ui.tsx';
import { DocLink } from '../shared/DocLink.tsx';
import { RecordPayment } from './RecordPayment.tsx';
import { useMirror } from '../shared/mirror.ts';
import { FinancingWaiting } from './FinancingWaiting.tsx';

type Row = ArAging['rows'][number];

function HoldForm({ row, act, onDone }: { row: Row; act: ReturnType<typeof useAct>; onDone: () => void }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fgrid inline-form">
      <label className="wide">
        <span className="flabel">{row.onHold ? 'Why release the hold?' : 'Why put them on hold?'}</span>
        <input
          className="field"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="A reason is required"
        />
      </label>
      <div className="row-actions">
        <button
          className="btn primary"
          disabled={act.busy || !reason.trim()}
          onClick={() =>
            void (async () => {
              const ok = await act.run(
                'customers.setCollectionsHold',
                { customerId: row.customerId, on: !row.onHold, reason: reason.trim() },
                row.onHold ? 'Hold released.' : 'On hold: no reminders, and booking shows a banner.',
              );
              if (ok) onDone();
            })()
          }
        >
          {row.onHold ? 'Release hold' : 'Put on hold'}
        </button>
        <button className="btn quiet" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function Receivables({ held, toast, timeZone }: BooksProps) {
  const aging = useQuery<ArAging>('receivables.aging', {});
  const due = useQuery<{ reminders: DueReminder[] }>('receivables.dueReminders', {});
  const act = useAct(toast, async () => {
    await Promise.all([aging.reload(), due.reload()]);
  });
  const [open, setOpen] = useState<{ id: string; form: 'pay' | 'hold' } | null>(null);
  const mirroring = useMirror();
  const [paid, setPaid] = useState(0); // a payment recorded here: financing re-reads (BL-005)
  const [filter, setFilter] = useState<'all' | 'overdue'>('overdue');
  const rowPrimary = useRowPrimary();

  const statement = (customerId: string, name: string) => downloadStatement(customerId, name, toast);

  // A payment or an invoice just recorded posts to the ledger a moment later (QA-508): while one is on
  // its way, read again every 2 s, up to 30 s, so the tie settles without anyone refreshing.
  const tie = aging.data ? arTie(aging.data) : null;
  const tries = useRef(0);
  useEffect(() => {
    if (tie?.state !== 'posting') {
      tries.current = 0;
      return;
    }
    if (tries.current >= 15) return;
    const t = setTimeout(() => {
      tries.current += 1;
      void aging.reload();
    }, 2000);
    return () => clearTimeout(t);
  }, [aging.data]);

  if (!aging.data)
    return <Loading error={aging.error} what="who owes you" retry={() => void aging.reload()} />;
  const a = aging.data;
  const rows = filter === 'overdue' ? a.rows.filter((r) => r.daysPastDue > 0) : a.rows;
  const max = Math.max(1, ...BUCKETS.map((b) => a.buckets[b]));
  return (
    <div className="books-page">
      <Panel
        title="Aging"
        aside={
          <span
            className={`chip say ${tie!.state === 'ties' ? 'ok' : tie!.state === 'posting' ? '' : 'warn'}`}
            role="status"
          >
            {tie!.state === 'ties' ? (
              'Ties to AR'
            ) : tie!.state === 'posting' ? (
              'Posting what was just recorded…'
            ) : (
              <>
                Differs from AR by <Money cents={a.differenceCents} />
                {tie!.failed ? ': a posting failed and the team has been told' : ''}
              </>
            )}
          </span>
        }
      >
        <div className="aging">
          {BUCKETS.map((b) => (
            <div key={b} className="aging-col">
              <span className="muted small">{BUCKET_LABEL[b]}</span>
              <Money cents={a.buckets[b]} className="mid" />
              <span
                className="bar"
                style={{ width: `${Math.round((a.buckets[b] / max) * 100)}%` }}
                aria-hidden="true"
              />
            </div>
          ))}
        </div>
        <p className="muted small">
          <Money cents={a.openInvoicesCents} /> open on {a.rows.length} invoice
          {a.rows.length === 1 ? '' : 's'}
          {a.unappliedCreditsCents ? (
            <>
              {' '}
              · <Money cents={a.unappliedCreditsCents} /> unapplied credit
            </>
          ) : null}
          {a.depositsCents ? (
            <>
              {' '}
              · <Money cents={a.depositsCents} /> deposits on sold estimates
            </>
          ) : null}
        </p>
        {/* the credits and deposits each against their account (BL-006; a difference is the ledger less the open rows); a posting on its way is said above */}
        {tie!.state !== 'posting' && (a.creditsDifferenceCents || a.depositsDifferenceCents) ? (
          <p className="notice warn small" role="status">
            {a.creditsDifferenceCents ? (
              <>
                Customer credits are <Money cents={Math.abs(a.creditsDifferenceCents)} />{' '}
                {a.creditsDifferenceCents > 0 ? 'less' : 'more'} than their account.{' '}
              </>
            ) : null}
            {a.depositsDifferenceCents ? (
              <>
                Customer deposits are <Money cents={Math.abs(a.depositsDifferenceCents)} />{' '}
                {a.depositsDifferenceCents > 0 ? 'less' : 'more'} than the Customer deposits account.
              </>
            ) : null}
          </p>
        ) : null}
      </Panel>

      <Panel
        title="Reminders due today"
        aside={due.data ? <span className="chip">{due.data.reminders.length}</span> : undefined}
      >
        {!due.data ? (
          <Loading error={due.error} what="today’s reminders" retry={() => void due.reload()} />
        ) : due.data.reminders.length === 0 ? (
          <Empty>No reminder is due today.</Empty>
        ) : (
          <ul className="cards">
            {due.data.reminders.map((r) => (
              <li key={r.invoiceId} className="stone reminder">
                <div>
                  <strong>
                    {r.customerName} ·{' '}
                    <DocLink doc={{ kind: 'invoice', id: r.invoiceId }}>{r.number}</DocLink>
                  </strong>
                  <span className="muted small">
                    <Money cents={r.balanceCents} /> · {r.daysPastDue} day{r.daysPastDue === 1 ? '' : 's'}{' '}
                    overdue
                    {r.channels.length
                      ? ` · ${r.channels.map((c) => (c === 'sms' ? 'text' : c)).join(' and ')}`
                      : ''}
                    {r.attachStatement && r.channels.includes('email') ? ' · statement attached' : ''}
                  </span>
                </div>
                <div className="row-actions">
                  {r.cannotSend ? (
                    <span className="lockwhy small">{r.cannotSend}</span>
                  ) : (
                    <button
                      className={rowPrimary}
                      disabled={act.busy}
                      onClick={() =>
                        void act.run(
                          'receivables.sendReminder',
                          { invoiceId: r.invoiceId, step: r.step },
                          `Reminder sent by ${r.channels.map((c) => (c === 'sms' ? 'text' : c)).join(' and ')}.`,
                        )
                      }
                    >
                      Send now
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Open invoices"
        aside={
          <div className="seg" role="group" aria-label="Show">
            <button
              className={`btn quiet${filter === 'overdue' ? ' on' : ''}`}
              onClick={() => setFilter('overdue')}
            >
              Overdue
            </button>
            <button className={`btn quiet${filter === 'all' ? ' on' : ''}`} onClick={() => setFilter('all')}>
              All
            </button>
          </div>
        }
      >
        {/* Overdue first, but never hiding that more are open: a just-sent invoice isn't due yet */}
        {filter === 'overdue' && a.rows.length > rows.length && (
          <p className="muted small">
            {a.rows.length - rows.length === 1
              ? '1 more open invoice isn’t due yet.'
              : `${a.rows.length - rows.length} more open invoices aren’t due yet.`}{' '}
            <button className="btn quiet" onClick={() => setFilter('all')}>
              Show all
            </button>
          </p>
        )}
        {rows.length === 0 ? (
          <Empty>{filter === 'overdue' ? 'Nothing is overdue.' : 'No open invoices.'}</Empty>
        ) : (
          <ul className="rows">
            {rows.map((r) => (
              <li key={r.invoiceId} className="inv-row">
                <div className="inv-main">
                  <div>
                    <strong>
                      <DocLink doc={{ kind: 'invoice', id: r.invoiceId }}>{r.number}</DocLink>
                    </strong>{' '}
                    <span>{r.customerName}</span>
                    {r.onHold && <span className="chip say warn">On hold</span>}
                  </div>
                  <span className="muted small">
                    Due {dayText(r.dueOn)}
                    {r.daysPastDue > 0 && (
                      <span className="overdue">
                        {` · ${r.daysPastDue} day${r.daysPastDue === 1 ? '' : 's'} overdue`}
                      </span>
                    )}
                  </span>
                </div>
                <Money cents={r.balanceCents} className="amount" />
                <RowActions>
                  {/* ServiceTitan takes its payments while the company mirrors it (BL-088) */}
                  {!mirroring && (
                    <button className="btn" onClick={() => setOpen({ id: r.invoiceId, form: 'pay' })}>
                      Record payment
                    </button>
                  )}
                  <button className="btn quiet" onClick={() => void statement(r.customerId, r.customerName)}>
                    Statement
                  </button>
                  <button className="btn quiet" onClick={() => setOpen({ id: r.invoiceId, form: 'hold' })}>
                    {r.onHold ? 'Release hold' : 'Hold'}
                  </button>
                </RowActions>
                {open?.id === r.invoiceId && open.form === 'pay' && (
                  <RecordPayment
                    customerId={r.customerId}
                    customerName={r.customerName}
                    firstInvoiceId={r.invoiceId}
                    timeZone={timeZone}
                    dates={held.includes('invoices.edit')}
                    invoices={a.rows.filter((x) => x.customerId === r.customerId)}
                    toast={toast}
                    onDone={(recorded) => {
                      setOpen(null);
                      if (recorded) {
                        void Promise.all([aging.reload(), due.reload()]);
                        setPaid((n) => n + 1);
                      }
                    }}
                  />
                )}
                {open?.id === r.invoiceId && open.form === 'hold' && (
                  <HoldForm row={r} act={act} onDone={() => setOpen(null)} />
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {/* BL-005: financed sales the lender hasn't fully paid out */}
      <FinancingWaiting toast={toast} timeZone={timeZone} refresh={paid} />
    </div>
  );
}
